import { useDispatch, useSelector } from 'react-redux';
import Skeleton from '@/components/primitives/Skeleton.jsx';
import toastManager from '@/lib/toast/toastManager.js';
import sendPrompt from '@/redux/actions/pieveActions/sendPrompt.js';
import { cancelThreadRun } from '@/redux/requests/threadRunRequests.js';
import { routeThreadId } from '@/redux/selectors/pieveSelectors/routeSelectors.js';
import { threadContent } from '@/redux/selectors/pieveSelectors/threadCoreSelectors.js';
import { isThreadRunning, threadActiveRunId } from '@/redux/selectors/pieveSelectors/threadRunSelectors.js';
import Composer from '../components/Composer.jsx';
import ConnectionReturnHandler from '../handlers/ConnectionReturnHandler.jsx';
import RunStatus from '../components/RunStatus.jsx';
import ThreadHeader from '../components/ThreadHeader.jsx';
import ThreadRail from '../components/ThreadRail.jsx';
import ThreadTimeline from '../components/ThreadTimeline.jsx';
import buildRequestInput from '../lib/buildRequestInput.js';

/** One conversation: what was asked, every step Pieve took, its replies and approvals, and where the latest run stands. */
const ThreadPage = () => {
  const dispatch = useDispatch();
  const threadId = useSelector(routeThreadId);
  const content = useSelector((state) => threadContent(state, threadId));
  const isRunning = useSelector((state) => isThreadRunning(state, threadId));
  const runId = useSelector((state) => threadActiveRunId(state, threadId));
  const isLoaded = Array.isArray(content?.activity);

  const send = async ({ prompt, attachments }) => {
    const res = isRunning && runId && !String(runId).startsWith('pending-') ? await dispatch(sendPrompt({ mode: 'steer', threadId, runId, prompt })) : await dispatch(sendPrompt({ threadId, prompt, newUserInput: buildRequestInput({ prompt, attachments }) }));
    if (res?.success === false) {
      toastManager.add({ title: 'Message not sent', description: res?.data?.message || res?.message || 'Try again.', type: 'error' });
    }
    return res;
  };

  const stop = async () => {
    const res = await cancelThreadRun({ threadId, runId });
    if (res?.success === false) {
      toastManager.add({ title: 'Could not stop', description: res.message || 'Try again.', type: 'error' });
    }
  };

  return (
    <div className="assistant-thread-layout">
      <ConnectionReturnHandler />
      <ThreadRail />
      <div className="assistant-thread">
        <ThreadHeader threadId={threadId} />
        <div className="assistant-thread__body">
          {isLoaded ? (
            <ThreadTimeline threadId={threadId} />
          ) : (
            <div className="assistant-thread__loading" aria-label="Loading the conversation">
              <Skeleton width="40%" />
              <Skeleton shape="block" height={96} />
              <Skeleton width="70%" />
            </div>
          )}
        </div>
        <div className="assistant-thread__dock">
          <RunStatus threadId={threadId} />
          <Composer key={threadId} threadId={threadId} isRunning={isRunning} onStop={runId ? stop : undefined} placeholder={isRunning ? 'Add a detail for Pieve' : 'Ask a follow-up'} onSubmit={send} />
        </div>
      </div>
    </div>
  );
};

export default ThreadPage;
